import React, { useState } from "react";
import translations from "../common/Translations";
import Tooltip from "../common/Tooltip";
import { getGroupsByDepartment } from "../../service/User";

export const getCurrentUserGroupIds = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem("userDetails")) || {};
    const raw = userDetails?.groupList || userDetails?.groups || userDetails?.groupByDepartmentList || [];
    if (!Array.isArray(raw)) return [];
    return raw
      .map((g) =>
        typeof g === "object" && g !== null
          ? g.id ?? g.groupId ?? g.groupByDepartmentId ?? g.groupByDepartment?.[0]?.groupByDepartmentId
          : g
      )
      .filter((id) => id !== undefined && id !== null && id !== "")
      .map((id) => String(id));
  } catch (e) {
    console.error("Error reading user group access:", e);
    return [];
  }
};

export const filterGroupsByUserAccess = (groups) => {
  const allowed = getCurrentUserGroupIds();
  if (!allowed.length) return Array.isArray(groups) ? groups : [];
  const allowedSet = new Set(allowed);
  return (Array.isArray(groups) ? groups : []).filter((g) =>
    allowedSet.has(String(g?.id ?? g?.groupId ?? g?.groupByDepartmentId))
  );
};

export const getCurrentUserType = () => {
  try {
    return JSON.parse(localStorage.getItem("userDetails"))?.userType ?? "";
  } catch (e) {
    return "";
  }
};

// Whether the logged-in user may see/use the Group feature (form field + assign tabs).
// Super admin: always. Admin / office user: only when they have at least one group assigned.
export const currentUserCanUseGroups = () => {
  if (getCurrentUserType() === "super_admin") return true;
  return getCurrentUserGroupIds().length > 0;
};

// Only super admin may create new groups; admin / office users can only pick existing ones.
export const currentUserCanCreateGroups = () => getCurrentUserType() === "super_admin";

// Groups are fetched per department (get-by-department); the department comes from localStorage.
// Shared by the assign, reassign and unassign task modals so their group tab behaves like the map view.
export const loadGroupsForDepartment = async () => {
  try {
    const departmentId = Number(
      String(localStorage.getItem("departmentId") ?? "").replace(/"/g, "").trim()
    );
    if (!departmentId || Number.isNaN(departmentId)) return [];
    const list = await getGroupsByDepartment(departmentId);
    return filterGroupsByUserAccess(Array.isArray(list) ? list : []);
  } catch (e) {
    console.error("Error fetching groups:", e);
    return [];
  }
};

// Normalize the group list from the get-by-department API into a shape the modals render.
// API group: { id, groupName, departmentId, userIds: [], members: [{ userId, userName }], projectId, projectIds, etc. }
export const normalizeGroups = (rawGroups) =>
  (Array.isArray(rawGroups) ? rawGroups : []).map((g) => {
    const rawMembers = Array.isArray(g?.members) ? g.members : [];
    const members = rawMembers.length > 0
      ? rawMembers.map((m) => ({
          id: m?.userId ?? m?.id ?? m,
          name: m?.userName ?? m?.name ?? "Unknown",
        }))
      : (Array.isArray(g?.userIds) ? g.userIds : []).map((uid) => ({
          id: uid,
          name: "Unknown",
        }));

    const projectList = Array.isArray(g?.projectList)
      ? g.projectList
      : Array.isArray(g?.projects)
      ? g.projects
      : Array.isArray(g?.projectIds)
      ? g.projectIds
      : Array.isArray(g?.projectIdList)
      ? g.projectIdList
      : [];

    const projectId =
      g?.projectId ??
      g?.project_id ??
      (typeof g?.project === "object" ? g?.project?.id ?? g?.project?.projectId : g?.project) ??
      (projectList.length > 0
        ? typeof projectList[0] === "object"
          ? projectList[0]?.id ?? projectList[0]?.projectId
          : projectList[0]
        : null);

    return {
      groupId: g?.id ?? g?.groupId ?? g?.groupByDepartmentId,
      groupName: g?.groupName || "-",
      members,
      projectId,
      projectList,
      projectIds: projectList,
      raw: g,
    };
  });

export const getGroupProjectIds = (group) => {
  const g = group?.raw || group;
  if (!g) return [];
  const ids = [];

  const addVal = (val) => {
    if (val === undefined || val === null) return;
    if (typeof val === "object") {
      const id = val.id ?? val.projectId ?? val.project_id ?? val.value;
      if (id !== undefined && id !== null && String(id).trim() !== "" && String(id) !== "NaN") {
        ids.push(String(id).trim());
      }
      if (val.name && String(val.name).trim() !== "") {
        ids.push(String(val.name).trim().toLowerCase());
      }
      return;
    }
    const str = String(val).trim();
    if (str !== "" && str !== "undefined" && str !== "null" && str !== "NaN") {
      ids.push(str);
      ids.push(str.toLowerCase());
    }
  };

  // Group-level scalar fields
  [
    group?.projectId,
    group?.project_id,
    group?.project,
    group?.projectName,
    group?.project_name,
    g?.projectId,
    g?.project_id,
    g?.project,
    g?.projectName,
    g?.project_name,
  ].forEach(addVal);

  // Group-level nested object fields
  [
    group?.project?.id,
    group?.project?.projectId,
    group?.project?.project_id,
    group?.project?.name,
    g?.project?.id,
    g?.project?.projectId,
    g?.project?.project_id,
    g?.project?.name,
  ].forEach(addVal);

  // Group-level array fields
  const arraySources = [
    group?.projectIds,
    group?.projectIdList,
    group?.projectList,
    group?.projects,
    group?.projectsList,
    group?.project_ids,
    group?.project_id_list,
    group?.project_list,
    g?.projectIds,
    g?.projectIdList,
    g?.projectList,
    g?.projects,
    g?.projectsList,
    g?.project_ids,
    g?.project_id_list,
    g?.project_list,
  ];

  arraySources.forEach((arr) => {
    if (Array.isArray(arr)) {
      arr.forEach(addVal);
    }
  });

  // Also check members
  const members = Array.isArray(g?.members)
    ? g.members
    : Array.isArray(group?.members)
    ? group.members
    : [];

  members.forEach((m) => {
    if (!m) return;
    addVal(m?.projectId);
    addVal(m?.project_id);
    addVal(m?.project);
    addVal(m?.project?.id);
    addVal(m?.project?.name);
    [m?.projectList, m?.projectIds, m?.projects, m?.projectIdList].forEach((arr) => {
      if (Array.isArray(arr)) arr.forEach(addVal);
    });
  });

  return [...new Set(ids)];
};

export const isGroupAssociatedWithProject = (group, targetProjectId) => {
  if (
    targetProjectId === undefined ||
    targetProjectId === null ||
    String(targetProjectId).trim() === "" ||
    String(targetProjectId) === "NaN"
  ) {
    return true;
  }

  const targets = Array.isArray(targetProjectId)
    ? targetProjectId
    : [targetProjectId];

  const targetStrs = targets
    .filter((t) => t !== undefined && t !== null && String(t).trim() !== "" && String(t) !== "NaN")
    .flatMap((t) => [String(t).trim(), String(t).trim().toLowerCase()]);

  if (targetStrs.length === 0) return true;

  const groupProjectIds = getGroupProjectIds(group);

  // If the group has project information:
  if (groupProjectIds.length > 0) {
    return groupProjectIds.some((id) => targetStrs.includes(id));
  }

  // If the group has NO project info at all in the API response, do not block it:
  return true;
};

// Field-agent visibility by the logged-in user's assigned groups.
// Admin / office users should only see field agents who belong to at least one of
// the groups assigned to them. A user with a blank group list (e.g. super admin, or
// an admin with no group restriction) is unrestricted and sees every field agent.
// Returns the list unchanged when unrestricted; otherwise filters to group members.
export const filterAgentsByCurrentUserGroups = async (agents) => {
  const list = Array.isArray(agents) ? agents : [];
  const groupIds = getCurrentUserGroupIds();
  if (!groupIds.length) return list; // blank group list -> show all field agents
  const groups = await loadGroupsForDepartment(); // already limited to the user's groups
  const allowedIds = getGroupMemberIdSet(groups);
  return list.filter((agent) =>
    allowedIds.has(String(agent?.userId ?? agent?.id ?? ""))
  );
};

// Collects the field-agent userIds that belong to the given (already access-filtered) groups.
export const getGroupMemberIdSet = (groups) =>
  new Set(
    normalizeGroups(groups)
      .flatMap((group) => group.members || [])
      .map((member) => String(member?.id ?? ""))
      .filter((id) => id && id !== "undefined" && id !== "null")
  );

// Extracts an assignee's id regardless of the field name the backend used.
const getAssigneeId = (assignee) =>
  String(assignee?.assigneeId ?? assignee?.userId ?? assignee?.id ?? "");

// Group-access visibility rule for a task, shared by the task list and the map view.
// allowedAgentIdSet === null => user is unrestricted (super admin / no assigned groups) => show everything.
// Otherwise a task is visible when:
//   - it is unassigned AND its status is "Active", or
//   - at least one of its assignees is a member of the user's accessible groups.
export const isTaskVisibleForGroupAccess = (task, allowedAgentIdSet) => {
  if (!allowedAgentIdSet) return true;
  const assignees = Array.isArray(task?.assignees) ? task.assignees : [];
  if (assignees.length === 0) {
    return task?.taskStatus === "Active";
  }
  return assignees.some((assignee) => allowedAgentIdSet.has(getAssigneeId(assignee)));
};

export const filterGroupsByAssignedMembers = (groups, assignedUserIds) => {
  const assignedSet = new Set(
    (Array.isArray(assignedUserIds) ? assignedUserIds : [])
      .filter((id) => id !== undefined && id !== null && id !== "")
      .map((id) => String(id))
  );
  if (!assignedSet.size) return [];
  return (Array.isArray(groups) ? groups : []).reduce((acc, group) => {
    const members = (Array.isArray(group?.members) ? group.members : []).filter((m) =>
      assignedSet.has(String(m?.id))
    );
    if (members.length) acc.push({ ...group, members });
    return acc;
  }, []);
};

// Does a group match the search query (by group name or any member name)?
export const groupMatchesQuery = (group, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  return (
    (group.groupName || "").toLowerCase().includes(q) ||
    group.members.some((m) => (m.name || "").toLowerCase().includes(q))
  );
};

// Users / Groups tab switcher shared by the assign, reassign and unassign modals.
// Hidden entirely when the logged-in user has no group access, so only the
// (default) "users" scope remains available to them.
export const AssignScopeTabs = ({ scope, onScopeChange, langMode, userLabel, groupLabel }) => {
  if (!currentUserCanUseGroups()) return null;
  return (
  <div className="flex w-full rounded-lg border border-slate-200 bg-slate-50 p-1 mb-3">
    {[
      { key: "users", label: userLabel ?? translations[langMode]?.userAssign ?? "User Assign" },
      { key: "groups", label: groupLabel ?? translations[langMode]?.groupAssign ?? "Group Assign" },
    ].map((tab) => (
      <button
        key={tab.key}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onScopeChange(tab.key);
        }}
        className={`flex-1 min-w-0 px-2 py-1.5 text-sm font-medium rounded-md transition whitespace-nowrap text-center cursor-pointer ${
          scope === tab.key
            ? "bg-[#236DB4] text-white shadow-sm"
            : "text-slate-500 hover:text-slate-700 hover:bg-white/60"
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
  );
};

// Renders a single-select accordion list of groups styled like the agent list (checkbox + avatar).
// The chosen group's id is sent to the backend as groupId; each group expands to show its members.
// `disabledGroupTooltip` is opt-in: callers that pass it get a hover explanation on
// rows that cannot be picked (no selectable members). Existing callers that omit it
// render exactly as before.
export const GroupSelectList = ({
  groups,
  selectedGroupId,
  onSelectGroup,
  query,
  langMode,
  allowedMemberIds,
  disabledGroupTooltip,
  selectedProjectId,
}) => {
  const [expanded, setExpanded] = useState({});

  if (!groups.length) {
    return (
      <p className="text-sm text-slate-400 py-4 text-center">
        {translations[langMode]?.nogroupfound ?? "No group found"}
      </p>
    );
  }

  const q = (query || "").trim().toLowerCase();
  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const allowedSet = allowedMemberIds
    ? new Set((Array.isArray(allowedMemberIds) ? allowedMemberIds : Array.from(allowedMemberIds)).map((id) => String(id)))
    : null;
  const isMemberAllowed = (m) => !allowedSet || allowedSet.has(String(m?.id));
  const noAccessText =
    translations[langMode]?.memberNoWorkflowAccess ?? "No access to this workflow";

  const notInProjectText =
    translations[langMode]?.groupNotInProject ?? "Not part of selected project";
  const notInProjectTooltipText =
    translations[langMode]?.groupNotInProjectTooltip ?? notInProjectText;
  const noMembersTooltipText =
    disabledGroupTooltip || translations[langMode]?.groupDisabledNoMembers || "Group has no members";

  const groupRows = groups.map((group) => {
    const isSelected = selectedGroupId != null && group.groupId === selectedGroupId;
    const hasMembers = group.members.length > 0;
    const nameMatches = q ? (group.groupName || "").toLowerCase().includes(q) : false;
    const matchingMembers = q
      ? group.members.filter((m) => (m.name || "").toLowerCase().includes(q))
      : [];

    const isOpen =
      hasMembers &&
      (expanded[group.groupId] ?? (matchingMembers.length > 0 && !nameMatches));
    const membersToShow = q && matchingMembers.length > 0 ? matchingMembers : group.members;
    
    const allowedMemberCount = group.members.filter((m) => isMemberAllowed(m)).length;
    const isProjectMatch =
      selectedProjectId == null || String(selectedProjectId).trim() === ""
        ? true
        : isGroupAssociatedWithProject(group, selectedProjectId);

    const isSelectable = isProjectMatch && allowedMemberCount > 0;
    const toggleSelect = () => {
      if (!isSelectable) return;
      onSelectGroup(isSelected ? null : group.groupId);
    };

    const tooltipText = !isProjectMatch
      ? notInProjectTooltipText
      : !isSelectable
      ? noMembersTooltipText
      : "";

    const showDisabledTooltip = Boolean(!isSelectable && tooltipText);
    // Tooltip renders a wrapper div, so it is only introduced when there is a
    // reason to show - the enabled rows keep their original DOM shape.
    const RowWrapper = showDisabledTooltip ? Tooltip : React.Fragment;
    const rowWrapperProps = showDisabledTooltip
      ? { text: tooltipText, position: "top", className: "w-full" }
      : {};

    return (
      <div key={group.groupId} className="rounded-lg">
        <RowWrapper {...rowWrapperProps}>
        <div
          className={`flex items-center gap-3 py-2 px-2 rounded-lg ${
            isSelected ? "bg-blue-50" : isSelectable ? "hover:bg-slate-50" : ""
          }`}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={toggleSelect}
            disabled={!isSelectable}
            className={`w-4 h-4 accent-[#236DB4] text-blue-600 rounded ${isSelectable ? "" : "cursor-not-allowed opacity-40"}`}
          />
          <button
            type="button"
            onClick={toggleSelect}
            disabled={!isSelectable}
            className={`flex flex-1 items-center gap-3 min-w-0 text-start ${isSelectable ? "" : "cursor-not-allowed opacity-60"}`}
          >
            <span className="h-8 w-8 shrink-0 rounded-full bg-[#236DB4] text-white flex items-center justify-center text-sm font-semibold">
              {String(group.groupName).charAt(0).toUpperCase()}
            </span>
            <span className="min-w-0 truncate text-sm font-medium text-slate-700">
              {group.groupName}
            </span>
            {!isProjectMatch && (
              <span className="shrink-0 text-xs italic text-red-500 font-normal">
                {notInProjectText}
              </span>
            )}
          </button>
          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
            {group.members.length}
          </span>
          <button
            type="button"
            onClick={() => hasMembers && toggleExpand(group.groupId)}
            disabled={!hasMembers}
            aria-label={isOpen ? "Collapse members" : "Expand members"}
            className={`shrink-0 p-1 text-slate-400 ${
              hasMembers
                ? "hover:text-slate-600"
                : "opacity-40 cursor-not-allowed"
            }`}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
        </div>
        </RowWrapper>
        {isOpen && (
          <div className="ml-11 mr-2 mb-2 border-l border-slate-200 pl-3">
            {membersToShow.length > 0 ? (
              membersToShow.map((m) => {
                const allowed = isMemberAllowed(m);
                return (
                  <div
                    key={m.id}
                    className={`flex items-center gap-2 py-1 ${allowed ? "" : "opacity-50"}`}
                  >
                    <span
                      className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                        allowed ? "bg-slate-200 text-slate-600" : "bg-slate-200 text-slate-500"
                      }`}
                    >
                      {String(m.name).charAt(0).toUpperCase()}
                    </span>
                    <span
                      className={`min-w-0 truncate text-xs ${
                        allowed ? "text-slate-700" : "text-slate-600"
                      }`}
                    >
                      {m.name}
                    </span>
                    {!allowed && (
                      <span className="shrink-0 text-[10px] italic text-slate-500">
                        {noAccessText}
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="py-1 text-xs text-slate-400">
                {translations[langMode]?.nomembers ?? "No members"}
              </p>
            )}
          </div>
        )}
      </div>
    );
  });

  return (
    <>
      {allowedSet && (
        <p className="mb-2 px-1 text-xs text-slate-500">
          {translations[langMode]?.assignWorkflowAccessNote ??
            "Only group members who have access to this workflow will be assigned the task."}
        </p>
      )}
      {groupRows}
    </>
  );
};
